import React, { useState, useEffect, useMemo } from 'react';
import { NavButton } from './NavButton';
import { Spinner } from './Spinner';

interface AvailabilityDay {
	date: string;
	isAvailable: boolean;
	status: string;
	price: number | null;
	minimumStay: number | null;
	maximumStay: number | null;
	closedOnArrival: boolean;
	closedOnDeparture: boolean;
}

interface PropertyAvailabilityCalendarProps {
	listingId: number;
	ajaxUrl: string;
	bookingUrl?: string;
	// Per-night prices in the day cells. Off unless a block instance asks for
	// them (block.json `showPrices`) -- the design shows the date alone.
	showPrices?: boolean;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// YYYY-MM-DD -> local midnight. Never `new Date(str)`, which parses the same
// string as UTC and so lands on the previous day west of Greenwich.
const parseDate = (dateStr: string): Date =>
	{
	const [year, month, day] = dateStr.split('-').map(Number);
	return new Date(year, month - 1, day);
	};

const formatDate = (date: Date): string =>
	{
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
	};

const nightsBetween = (startStr: string, endStr: string): number =>
	Math.round((parseDate(endStr).getTime() - parseDate(startStr).getTime()) / MS_PER_DAY);

const formatPrice = (price: number | null): string => (price ? `£${price}` : 'N/A');

const formatLongDate = (dateStr: string): string =>
	parseDate(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export const PropertyAvailabilityCalendar = ({ listingId, ajaxUrl, bookingUrl, showPrices = false }: PropertyAvailabilityCalendarProps) => {
	const today = useMemo(() =>
		{
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
		}, []);

	const [currentMonth, setCurrentMonth] = useState(today.getMonth());
	const [currentYear, setCurrentYear] = useState(today.getFullYear());
	const [availabilityData, setAvailabilityData] = useState<{ [key: string]: AvailabilityDay }>({});
	const [loading, setLoading] = useState(true);
	const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
	const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);
	const [hoverDate, setHoverDate] = useState<string | null>(null);
	const [rangeError, setRangeError] = useState<string | null>(null);
	const [guests, setGuests] = useState(2);

	// Read the dates/guests carried over from a property-results search (see
	// buildPropertyUrl() in property-results/src/components/SearchResults.tsx)
	// so the calendar arrives with that stay already selected -- not just
	// highlighted -- so Book Now works without the visitor re-picking dates.
	useEffect(() =>
		{
		const params = new URLSearchParams(window.location.search);
		const checkIn = params.get('check_in');
		const checkOut = params.get('check_out');
		const guestsParam = params.get('guests');

		if (checkIn && checkOut)
			{
			// Convert MM/DD/YYYY to YYYY-MM-DD
			const convertDate = (dateStr: string) =>
				{
				const [month, day, year] = dateStr.split('/');
				return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
				};

			const start = convertDate(checkIn);
			const end = convertDate(checkOut);

			setSelectedStartDate(start);
			setSelectedEndDate(end);

			// Open on the month the stay starts in rather than on today, so the
			// arriving selection is actually on screen.
			const startDate = parseDate(start);
			setCurrentMonth(startDate.getMonth());
			setCurrentYear(startDate.getFullYear());
			}

		if (guestsParam)
			{
			const parsedGuests = parseInt(guestsParam, 10);
			if (parsedGuests > 0)
				{
				setGuests(parsedGuests);
				}
			}
		}, []);

	useEffect(() =>
		{
		fetchAvailability();
		}, [currentMonth, currentYear]);

	const fetchAvailability = async () =>
		{
		setLoading(true);
		// Fetch the displayed month plus the next one, so a stay that runs over
		// the month boundary can be validated without navigating first.
		const startDate = new Date(currentYear, currentMonth, 1);
		const endDate = new Date(currentYear, currentMonth + 2, 0);

		const params = new URLSearchParams({
			action: 'get_property_availability',
			listing_id: listingId.toString(),
			start_date: formatDate(startDate),
			end_date: formatDate(endDate),
		});

		try
			{
			const response = await fetch(`${ajaxUrl}?${params}`);
			const data = await response.json();

			if (data.success)
				{
				const dataMap: { [key: string]: AvailabilityDay } = {};
				data.data.forEach((day: AvailabilityDay) =>
					{
					dataMap[day.date] = day;
					});
				// Merge, never replace: a stay spanning two months is validated
				// against nights that belong to a month we've already moved
				// away from, and dropping them made every cross-month range
				// look unbookable.
				setAvailabilityData((previous) => ({ ...previous, ...dataMap }));
				}
			}
		catch (error)
			{
			console.error('Failed to fetch availability:', error);
			}
		finally
			{
			setLoading(false);
			}
		};

	const getDayInfo = (date: Date): AvailabilityDay | null => availabilityData[formatDate(date)] || null;

	const isDateInSelectionRange = (dateStr: string): boolean =>
		{
		if (!selectedStartDate) return false;

		const endDate = selectedEndDate || hoverDate;
		if (!endDate) return false;

		const start = selectedStartDate < endDate ? selectedStartDate : endDate;
		const end = selectedStartDate < endDate ? endDate : selectedStartDate;

		return dateStr >= start && dateStr <= end;
		};

	// Free on the night itself and open to arrivals -- but that alone doesn't
	// make a day bookable. See canCheckIn() below, which is what the UI uses.
	const isArrivalDay = (dateStr: string): boolean =>
		{
		const dayInfo = availabilityData[dateStr];
		return !!dayInfo?.isAvailable && !dayInfo.closedOnArrival;
		};

	// A day you can actually start a stay on: the shortest permitted stay
	// starting here has to fit before the next booked night. Without this a day
	// sitting one or two nights before a booking is offered as a check-in, and
	// every check-out the visitor then tries is rejected -- e.g. a 3-night
	// minimum on a Friday when the Sunday is already gone.
	//
	// Walks forward from the minimum-stay checkout and out to the longest stay
	// allowed, because closedOnDeparture can rule out the earliest candidate
	// while a later one is still fine. getRangeError() is the single source of
	// truth for what makes a range legal, so this can't drift from what
	// selecting the dates would actually do.
	const canCheckIn = (dateStr: string): boolean =>
		{
		if (!isArrivalDay(dateStr)) return false;

		const dayInfo = availabilityData[dateStr];
		const minNights = dayInfo?.minimumStay && dayInfo.minimumStay > 0 ? dayInfo.minimumStay : 1;
		const maxNights = dayInfo?.maximumStay && dayInfo.maximumStay > 0 ? dayInfo.maximumStay : minNights;

		for (let nights = minNights; nights <= maxNights; nights++)
			{
			const checkout = parseDate(dateStr);
			checkout.setDate(checkout.getDate() + nights);
			const checkoutStr = formatDate(checkout);

			// Past the end of the loaded window -- can't prove it's unbookable,
			// so don't grey it out on a guess.
			if (!availabilityData[checkoutStr]) return true;
			if (getRangeError(dateStr, checkoutStr) === null) return true;
			}

		return false;
		};

	// null = the range is bookable. Otherwise the reason, shown to the visitor
	// instead of silently throwing their check-in away.
	const getRangeError = (startStr: string, endStr: string): string | null =>
		{
		// isArrivalDay(), not canCheckIn() -- canCheckIn() is defined in terms of
		// this function, so calling it here would recurse forever. The
		// minimum-stay rule it adds is enforced below anyway, for this specific
		// range rather than for any hypothetical one.
		if (endStr <= startStr) return 'Check-out has to be after check-in.';
		if (!isArrivalDay(startStr)) return 'That check-in date isn’t available.';

		const startDay = availabilityData[startStr];
		const nights = nightsBetween(startStr, endStr);

		if (startDay.minimumStay && nights < startDay.minimumStay)
			{
			return `This stay has a ${startDay.minimumStay}-night minimum from ${formatLongDate(startStr)}.`;
			}
		if (startDay.maximumStay && nights > startDay.maximumStay)
			{
			return `This stay has a ${startDay.maximumStay}-night maximum.`;
			}

		// Every night between check-in and check-out has to be free. The
		// check-out date itself is not a night, so it may well be booked.
		const cursor = parseDate(startStr);
		cursor.setDate(cursor.getDate() + 1);

		while (formatDate(cursor) < endStr)
			{
			const night = availabilityData[formatDate(cursor)];
			if (!night) return 'We don’t have availability loaded that far ahead yet.';
			if (!night.isAvailable) return 'Some nights in that range are already booked.';
			cursor.setDate(cursor.getDate() + 1);
			}

		if (availabilityData[endStr]?.closedOnDeparture) return 'Check-out isn’t possible on that date.';

		return null;
		};

	// Stated in the check-out prompt so the rule arrives before the click it
	// would otherwise reject. 1 means "no minimum worth mentioning".
	const minimumStayFrom = (dateStr: string): number =>
		{
		const min = availabilityData[dateStr]?.minimumStay;
		return min && min > 1 ? min : 1;
		};

	// A booked day can still be a departure day -- you're gone before the next
	// guest arrives -- so offer it as check-out even though it can't be a
	// check-in.
	const canCheckOut = (dateStr: string): boolean =>
		!!selectedStartDate && !selectedEndDate && dateStr > selectedStartDate && getRangeError(selectedStartDate, dateStr) === null;

	const startNewSelection = (dateStr: string) =>
		{
		setSelectedStartDate(dateStr);
		setSelectedEndDate(null);
		setHoverDate(null);
		setRangeError(null);
		};

	const handleDateClick = (dateStr: string) =>
		{
		// Completing a range
		if (selectedStartDate && !selectedEndDate && dateStr > selectedStartDate)
			{
			const error = getRangeError(selectedStartDate, dateStr);

			if (!error)
				{
				setSelectedEndDate(dateStr);
				setHoverDate(null);
				setRangeError(null);
				return;
				}

			// Say why rather than quietly restarting from the clicked date --
			// that reset was indistinguishable from the calendar being broken.
			setRangeError(error);
			return;
			}

		if (!canCheckIn(dateStr)) return;

		startNewSelection(dateStr);
		};

	const handleDateHover = (dateStr: string | null) =>
		{
		if (selectedStartDate && !selectedEndDate)
			{
			setHoverDate(dateStr);
			}
		};

	const clearSelection = () =>
		{
		setSelectedStartDate(null);
		setSelectedEndDate(null);
		setHoverDate(null);
		setRangeError(null);
		};

	const calculateTotalPrice = (): number | null =>
		{
		if (!selectedStartDate || !selectedEndDate) return null;

		let total = 0;
		const cursor = parseDate(selectedStartDate);
		const end = parseDate(selectedEndDate);

		while (cursor < end)
			{
			const dayData = availabilityData[formatDate(cursor)];
			if (dayData?.price)
				{
				total += dayData.price;
				}
			cursor.setDate(cursor.getDate() + 1);
			}

		return total > 0 ? total : null;
		};

	const calculateNights = (): number | null =>
		selectedStartDate && selectedEndDate ? nightsBetween(selectedStartDate, selectedEndDate) : null;

	const getBookingUrl = (): string =>
		{
		if (!bookingUrl || !selectedStartDate || !selectedEndDate) return '#';

		const params = new URLSearchParams({
			start: selectedStartDate,
			end: selectedEndDate,
			numberOfGuests: guests.toString(),
			page: '1',
		});

		return `${bookingUrl}?${params}`;
		};

	const renderMonth = (monthOffset: number = 0) =>
		{
		let month = currentMonth + monthOffset;
		let year = currentYear;

		if (month > 11)
			{
			month -= 12;
			year += 1;
			}
		else if (month < 0)
			{
			month += 12;
			year -= 1;
			}

		// Weeks start Monday, but Date#getDay() is Sunday-indexed (0-6) — shift so Monday is 0.
		const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		const days: React.ReactNode[] = [];

		// Empty cells for days before month starts
		for (let i = 0; i < firstDay; i++)
			{
			days.push(<div key={`empty-${i}`} className="property-availability__day property-availability__day--empty" />);
			}

		// Render days of month
		for (let day = 1; day <= daysInMonth; day++)
			{
			const date = new Date(year, month, day);
			const dateStr = formatDate(date);
			const isPast = date < today;
			const dayInfo = getDayInfo(date);
			const isStartDate = selectedStartDate === dateStr;
			const isEndDate = selectedEndDate === dateStr;
			const inSelectionRange = isDateInSelectionRange(dateStr);
			const checkInOption = !isPast && canCheckIn(dateStr);
			// Whether the day can legally end the stay, asked plainly. NOT
			// `&& !checkInOption` -- most valid check-outs are also perfectly
			// good check-in days, and excluding them left only the handful that
			// could do nothing else, which is the opposite of the intent.
			const canEndStayHere = !isPast && canCheckOut(dateStr);

			// Mid-selection, "available" stops meaning "free" and starts meaning
			// "a stay starting on the chosen date can end here". A night that is
			// free but too early to satisfy the minimum stay -- or sits the far
			// side of a booking -- is not offerable, so it reads as unavailable
			// for as long as the pick is open rather than inviting a click that
			// can only be refused.
			const pickingCheckOut = !!selectedStartDate && !selectedEndDate;
			const offerable = pickingCheckOut
				? (dateStr === selectedStartDate || canEndStayHere)
				: !!dayInfo?.isAvailable;

			// The half-white/half-grey split is specifically for a day that is
			// booked as a night yet still reachable as a departure -- it says
			// "you can leave here, but not stay". A day that is simply free
			// needs no such hint, so it keeps the plain available treatment.
			const checkOutOption = canEndStayHere && !dayInfo?.isAvailable;

			let className = 'property-availability__day';
			if (isPast) className += ' property-availability__day--past';
			if (offerable) className += ' property-availability__day--available';
			if (!offerable && !isPast) className += ' property-availability__day--unavailable';
			if (checkOutOption) className += ' property-availability__day--checkout-option';
			if (isStartDate) className += ' property-availability__day--range-start';
			if (isEndDate) className += ' property-availability__day--range-end';
			if (inSelectionRange && !isStartDate && !isEndDate) className += ' property-availability__day--in-selection';
			// Marks the days a stay can actually start on, per the design's
			// "check in day" key. Purely the visual half of a rule already
			// enforced: handleDateClick() ignores anything canCheckIn() rejects,
			// and those days render disabled. Suppressed once a start date is
			// chosen -- the question then is where the stay ends, so dotting
			// every other valid start would just be noise.
			if (checkInOption && !selectedStartDate) className += ' property-availability__day--checkin-option';

			// Mid-selection only the check-out question matters: a day that
			// could start some other stay is irrelevant until this one is
			// finished or abandoned.
			const isDisabled = isPast || loading || (pickingCheckOut
				? !canEndStayHere
				: !checkInOption);

			let label = `${day} ${monthNames[month]} ${year}`;
			if (isStartDate) label += ' — selected check-in';
			else if (isEndDate) label += ' — selected check-out';
			else if (pickingCheckOut) label += canEndStayHere ? ' — available as check-out' : ' — unavailable';
			else if (!checkInOption) label += ' — unavailable';

			days.push(
				<button
					key={day}
					type="button"
					className={className}
					onClick={() => handleDateClick(dateStr)}
					onMouseEnter={() => handleDateHover(dateStr)}
					onMouseLeave={() => handleDateHover(null)}
					disabled={isDisabled}
					aria-label={label}
					aria-pressed={isStartDate || isEndDate}
				>
					<span className="property-availability__day-number">{day}</span>
					{showPrices && dayInfo?.price && (
						<span className="property-availability__day-price">{formatPrice(dayInfo.price)}</span>
					)}
				</button>
			);
			}

		return (
			<div key={`month-${monthOffset}`} className="property-availability__month">
				<div className="property-availability__weekdays">
					<span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
				</div>
				<div className="property-availability__grid">
					{days}
				</div>
			</div>
		);
		};

	// Don't let the visitor page back before the current month -- there is
	// nothing bookable there and the empty grid reads as a loading failure.
	const atEarliestMonth = currentYear === today.getFullYear() && currentMonth === today.getMonth();

	const prevMonth = () =>
		{
		if (atEarliestMonth) return;

		if (currentMonth === 0)
			{
			setCurrentMonth(11);
			setCurrentYear(currentYear - 1);
			}
		else
			{
			setCurrentMonth(currentMonth - 1);
			}
		};

	const nextMonth = () =>
		{
		if (currentMonth === 11)
			{
			setCurrentMonth(0);
			setCurrentYear(currentYear + 1);
			}
		else
			{
			setCurrentMonth(currentMonth + 1);
			}
		};

	const totalPrice = calculateTotalPrice();
	const nights = calculateNights();

	return (
		<div className="property-availability__calendar">
			<div className="property-availability__header">
				<NavButton direction="prev" onClick={prevMonth} disabled={loading || atEarliestMonth} />
				<p className="property-availability__title">
					{monthNames[currentMonth]} {currentYear}
				</p>
				<NavButton direction="next" onClick={nextMonth} disabled={loading} />
			</div>

			{/* The grid stays mounted while a month loads -- swapping it for the
			    spinner collapsed the panel and bounced the whole page. */}
			<div className={`property-availability__months${loading ? ' property-availability__months--loading' : ''}`}>
				{renderMonth(0)}
				{loading && (
					<div className="property-availability__loading" aria-live="polite">
						<Spinner />
					</div>
				)}
			</div>

			{/*
				Order follows the design's own 2x2 reading order (node 17:151):
				available / selected dates on the top row, unavailable / check
				in day beneath.
			*/}
			<div className="property-availability__legend">
				<div className="property-availability__legend-item">
					<span className="property-availability__legend-box property-availability__legend-box--available"></span>
					<span>Available</span>
				</div>
				<div className="property-availability__legend-item">
					<span className="property-availability__legend-box property-availability__legend-box--selected"></span>
					<span>Selected dates</span>
				</div>
				<div className="property-availability__legend-item">
					<span className="property-availability__legend-box property-availability__legend-box--unavailable"></span>
					<span>Unavailable</span>
				</div>
				<div className="property-availability__legend-item">
					<span className="property-availability__legend-box property-availability__legend-box--checkin"></span>
					<span>Check in day</span>
				</div>
			</div>

			{selectedStartDate && (
				<div className="property-availability__selection-info">
					{selectedStartDate && selectedEndDate && (
						<div className="property-availability__selection-summary">
							<div className="property-availability__selection-dates">
								<div className="property-availability__selection-date">
									<span className="property-availability__selection-label">Check-in</span>
									<strong>{formatLongDate(selectedStartDate)}</strong>
								</div>
								<div className="property-availability__selection-date">
									<span className="property-availability__selection-label">Check-out</span>
									<strong>{formatLongDate(selectedEndDate)}</strong>
								</div>
								<div className="property-availability__selection-date">
									<span className="property-availability__selection-label">Nights</span>
									<strong>{nights}</strong>
								</div>
								{totalPrice && (
									<div className="property-availability__selection-date">
										<span className="property-availability__selection-label">Total</span>
										<strong>{formatPrice(totalPrice)}</strong>
									</div>
								)}
							</div>
							<div className="property-availability__selection-actions">
								{bookingUrl && (
									<a
										href={getBookingUrl()}
										target="_blank"
										rel="noopener noreferrer"
										className="wp-block-button__link property-availability__book-button"
									>
										Book Now
									</a>
								)}
								<button
									type="button"
									className="property-availability__clear-button"
									onClick={clearSelection}
								>
									Clear dates
								</button>
							</div>
						</div>
					)}
					{selectedStartDate && !selectedEndDate && (
						<div className="property-availability__selection-hint">
							Check-in <strong>{parseDate(selectedStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</strong>. Now pick your check-out date
							{minimumStayFrom(selectedStartDate) > 1 && (
								<> — <strong>{minimumStayFrom(selectedStartDate)} nights</strong> minimum</>
							)}.
						</div>
					)}
					{rangeError && (
						<p className="property-availability__selection-error" role="status">
							{rangeError}
						</p>
					)}
					{selectedStartDate && !selectedEndDate && (
						<button
							type="button"
							className="property-availability__clear-button property-availability__clear-button--inline"
							onClick={clearSelection}
						>
							Start again
						</button>
					)}
				</div>
			)}
		</div>
	);
};
